import React, { Fragment } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { AuthEventData } from '@aws-amplify/ui';
import { useNavigate } from 'react-router-dom';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import cx from 'classnames';

import GreenLogo from 'assets/images/oc-green-logo.svg';
import { NavigationItemInterface } from 'types/layout';

function DesktopSidebar({
  navigation,
  userName,
  companyName,
  nameInitial,
  signOut
}: {
  navigation: NavigationItemInterface[];
  userName: string;
  companyName: string;
  nameInitial: string;
  signOut: ((data?: AuthEventData | undefined) => void) | undefined;
}): JSX.Element {
  const navigate = useNavigate();

  return (
    <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col lg:border-r lg:border-gray-200 lg:bg-gray-100 lg:pt-5 lg:pb-4">
      <div className="flex flex-shrink-0 items-center px-6">
        <img src={GreenLogo} />
      </div>
      <div className="mt-5 flex h-0 flex-1 flex-col overflow-y-auto pt-1">
        <Menu as="div" className="relative inline-block px-3 text-left">
          <div>
            <Menu.Button className="group w-full rounded-md bg-gray-100 px-3.5 py-2 text-left text-sm font-medium text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-gray-100">
              <span className="flex w-full items-center justify-between">
                <span className="flex min-w-0 items-center justify-between space-x-3">
                  <div className="h-10 w-10 flex-shrink-0 rounded-full bg-gray-600 flex items-center justify-center text-sm text-white">
                    {nameInitial}
                  </div>
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-sm font-medium text-gray-900">{userName}</span>
                    <span className="truncate text-sm text-gray-500">{companyName}</span>
                  </span>
                </span>
                <ChevronDownIcon
                  className="h-5 w-5 flex-shrink-0 text-gray-400 group-hover:text-gray-500"
                  aria-hidden="true"
                />
              </span>
            </Menu.Button>
          </div>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95">
            <Menu.Items className="absolute right-0 left-0 z-10 mx-3 mt-1 origin-top divide-y divide-gray-200 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
              <div className="py-1">
                <Menu.Item>
                  {({ active }) => (
                    <a
                      className={cx('block px-4 py-2 text-sm cursor-pointer', {
                        'bg-gray-100 text-gray-900': active,
                        'text-gray-700': !active
                      })}
                      onClick={signOut}>
                      Sign out
                    </a>
                  )}
                </Menu.Item>
              </div>
            </Menu.Items>
          </Transition>
        </Menu>
        <nav className="mt-6 px-3">
          <div className="space-y-1">
            {navigation.map((item) => (
              <a
                key={item.name}
                onClick={() => navigate(item.href)}
                className={cx(
                  'group flex items-center rounded-md px-2 py-2 text-sm font-medium cursor-pointer',
                  {
                    'bg-gray-200 text-gray-900': item.current,
                    'text-gray-700 hover:bg-gray-50 hover:text-gray-900': !item.current
                  }
                )}
                aria-current={item.current ? 'page' : undefined}>
                <item.icon
                  className={cx('mr-3 h-6 w-6 flex-shrink-0', {
                    'text-gray-500': item.current,
                    'text-gray-400 group-hover:text-gray-500': !item.current
                  })}
                  aria-hidden="true"
                />
                {item.name}
              </a>
            ))}
          </div>
        </nav>
      </div>
    </div>
  );
}

export default DesktopSidebar;
